import { Link, useLocation } from 'react-router';

import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import Stack from '@mui/material/Stack';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';

import { paths } from 'src/routes/paths';

import { fonts } from 'src/theme';
import { neutralLight } from 'src/theme/palette';
import ltaLogo from 'src/assets/images/lta-logo.png';

import { DEFAULT_PROFILE_COLOUR } from 'src/components/profile-colour-modal/ProfileColourModal';

const NAV_ITEMS = [
  { label: 'Dashboard', key: 'dashboard', path: paths.dashboard },
  { label: 'Skills Profile', key: 'skills', path: paths.skillsProfile },
  { label: 'CV', key: 'cv', path: paths.cv },
  { label: 'Explore', key: 'explore', path: paths.explore },
];

const FOCUS_STYLES = {
  outline: '2px solid',
  outlineColor: 'primary.main',
  outlineOffset: 4,
};

type DashboardNavMenuProps = {
  userName: string;
  userInitial?: string;
  avatarColour?: string;
  onClose: () => void;
  onSignOut: () => void;
  id?: string;
  maxWidth?: number | string;
};

function UserInitial({ initial, avatarColour }: { initial: string; avatarColour?: string }) {
  return (
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: '50%',
        bgcolor: avatarColour ?? DEFAULT_PROFILE_COLOUR,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Typography
        sx={{
          fontSize: 18,
          fontWeight: 600,
          fontFamily: fonts.body,
          color: neutralLight.lightest,
          textTransform: 'uppercase',
          lineHeight: 1,
        }}
      >
        {initial}
      </Typography>
    </Box>
  );
}

export default function DashboardNavMenu({
  userName,
  userInitial,
  avatarColour,
  onClose,
  onSignOut,
  id = 'dashboard-navigation-menu',
  maxWidth,
}: DashboardNavMenuProps) {
  const location = useLocation();
  const initial = (userInitial ?? userName.trim().charAt(0).toUpperCase()) || '?';

  return (
    <Modal open onClose={onClose}>
      <Box
        id={id}
        role="dialog"
        aria-modal="true"
        aria-label="Main navigation menu"
        sx={{
          position: 'fixed',
          inset: 0,
          bgcolor: 'background.default',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth,
            display: 'flex',
            flexDirection: 'column',
            p: { xs: 0, md: 1 },
            pt: { xs: 0, md: 2 },
          }}
        >
          <Box
            sx={{
              flex: 1,
              bgcolor: 'background.paper',
              borderRadius: { xs: 0, md: 4 },
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              boxShadow: { xs: 0, md: 1 },
              position: 'relative',
              pt: 3,
              pb: 5,
              gap: 4,
            }}
          >
            <Box
              sx={{
                position: 'relative',
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 3,
                py: 1,
              }}
            >
              <Box sx={{ display: 'inline-flex', color: 'primary.main' }}>
                <Box component="img" src={ltaLogo} alt="" width={53} height={32} />
              </Box>
              <IconButton
                autoFocus
                onClick={onClose}
                aria-label="Close menu"
                sx={{ color: 'primary.main' }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <Stack spacing={6} sx={{ position: 'relative', zIndex: 1, px: 3, pt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <UserInitial initial={initial} avatarColour={avatarColour} />
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {userName}
                </Typography>
              </Box>

              <Stack component="nav" aria-label="Main navigation" spacing={4}>
                {NAV_ITEMS.map((item) => {
                  const active = location.pathname === item.path;
                  return (
                    <Typography
                      key={item.key}
                      component={Link}
                      to={item.path}
                      onClick={onClose}
                      aria-current={active ? 'page' : undefined}
                      variant="h2"
                      sx={{
                        width: 'fit-content',
                        cursor: 'pointer',
                        color: active ? 'primary.main' : 'neutralDark.medium',
                        fontSize: '1.125rem',
                        fontFamily: fonts.body,
                        textDecoration: 'none',
                        '&:focus-visible': FOCUS_STYLES,
                      }}
                    >
                      {item.label}
                    </Typography>
                  );
                })}
              </Stack>

              <Stack spacing={3}>
                <ButtonBase
                  onClick={onSignOut}
                  sx={{
                    width: 'fit-content',
                    justifyContent: 'flex-start',
                    // fontFamily: fonts.body,
                    // fontSize: 16,
                    lineHeight: 1.5,
                    letterSpacing: '0.2px',
                    fontWeight: 500,
                    color: 'neutralDark.medium',
                    '&:focus-visible': FOCUS_STYLES,
                    cursor: 'pointer',
                    fontSize: 16,
                    textDecoration: 'underline',
                    fontFamily: fonts.body,
                  }}
                >
                  Sign out
                </ButtonBase>
              </Stack>
            </Stack>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}
